export const VALID_STATUSES = ['New', 'Open', 'Pending', 'Awaiting Client Input', 'Closed'];

/**
 * The writable status set: CODE defaults UNION the published Admin Panel
 * statusValues (admin_config, additive — mirrors the SPA's mergeByKey). A
 * governed status must be accepted here or it shows in the dropdown yet every
 * write 400s. Fail-soft: any config problem falls back to the code defaults,
 * so a broken config row can never block core status changes.
 */
export async function resolveValidStatuses(pool, sql) {
  try {
    const { recordset } = await new sql.Request(pool)
      .input('key', sql.NVarChar(100), 'statusValues')
      .query('SELECT config_value FROM admin_config WHERE config_key = @key');
    const raw = recordset?.[0]?.config_value;
    if (raw == null) return [...VALID_STATUSES];
    const parsed = JSON.parse(raw);
    const configured = (Array.isArray(parsed) ? parsed : [])
      .map(s => (typeof s?.value === 'string' ? s.value.trim() : ''))
      .filter(Boolean);
    return [...new Set([...VALID_STATUSES, ...configured])];
  } catch {
    return [...VALID_STATUSES];
  }
}

// Stored status -> the short key the case-list URL uses (`?status=new,pending`).
// One map, imported by the SPA through `@shared` and by the by-clinic rollup, so
// a count and the link that drills into it can never disagree about which
// statuses they mean.
//
// KEYED IS A SUBSET OF VALID, AND NEITHER CONTAINS THE OTHER. Do not derive this
// from VALID_STATUSES, and do not derive VALID_STATUSES from this:
//   - 'With Claims' is keyed and drives a chip, but is a GOVERNED status living
//     in admin_config (see resolveValidStatuses), so it is absent from the code
//     defaults above.
//   - 'Awaiting Client Input' is still a code default because legacy rows may
//     carry it, but it was retired from the UI and has NO chip — so it gets no
//     key here. Giving it one would emit a count linking to a filtered list that
//     cannot show it (CaseList's fail-closed else-branch hides unchipped
//     statuses whenever any narrowing is active), i.e. a link whose count
//     contradicts itself.
// Anything unmapped returns null from statusUrlKey() and is counted in the
// totals without being itemised — deliberately mirroring that same else-branch.
export const STATUS_URL_KEY = {
  'New': 'new',
  'Open': 'open',
  'Pending': 'pending',
  'With Claims': 'claims',
  'Closed': 'closed',
};

/** URL keys in display order — the SPA's chip order, stated once. */
export const STATUS_KEYS = ['new', 'open', 'pending', 'claims', 'closed'];

/** The four non-Closed keys — what "active" means, spelled out. */
export const ACTIVE_STATUS_KEYS = STATUS_KEYS.filter(k => k !== 'closed');

export function statusUrlKey(raw) {
  return STATUS_URL_KEY[normaliseStatus(raw)] || null;
}

export function normaliseStatus(raw) {
  if (!raw || raw.trim() === '') return 'New';
  if (raw === 'Triage') return 'Pending';   // pre-migration-017 name for Pending
  if (VALID_STATUSES.includes(raw)) return raw;
  return raw; // pass through unknown values
}

export function isActive(status) {
  return normaliseStatus(status) !== 'Closed';
}
